/**
 * Scrollable, responsive year selector used by the calendar header.
 */

import * as React from "react";
import { cn } from "../../../../lib/utils";
import { Icon } from "../../../icon";
import { isYearSelectable } from "../../shared/calendar-model";
import type { SelectableDates } from "../../shared/types";

interface YearPickerListProps {
	selectedYear: number;
	yearRange: [number, number];
	selectableDates: SelectableDates;
	onYearSelect(year: number): void;
}

export const YearPickerList: React.FC<YearPickerListProps> = ({
	selectedYear,
	yearRange,
	selectableDates,
	onYearSelect,
}) => {
	const selectedRef = React.useRef<HTMLButtonElement>(null);
	const years = React.useMemo(() => {
		const [min, max] = yearRange;
		return Array.from({ length: max - min + 1 }, (_, index) => min + index);
	}, [yearRange]);

	React.useEffect(() => {
		selectedRef.current?.scrollIntoView?.({
			block: "center",
			behavior: "instant",
		});
	}, []);

	return (
		<div
			className="h-full min-h-[240px] overflow-y-auto py-2"
			role="listbox"
			aria-label="Select year"
		>
			{years.map((year) => {
				const isSelected = year === selectedYear;
				const isSelectable = isYearSelectable(year, selectableDates);
				return (
					<button
						key={year}
						ref={isSelected ? selectedRef : undefined}
						type="button"
						role="option"
						aria-selected={isSelected}
						aria-disabled={!isSelectable || undefined}
						disabled={!isSelectable}
						onClick={() => onYearSelect(year)}
						className={cn(
							"flex min-h-12 w-full items-center gap-4 px-6 text-left",
							"text-[length:var(--md-typescale-body-large-size,16px)]",
							"text-m3-on-surface transition-m3-fast-effects",
							"hover:bg-m3-on-surface/8 focus-visible:outline-2 focus-visible:outline-m3-primary",
							isSelected &&
								"bg-m3-secondary-container text-m3-on-secondary-container",
							!isSelectable && "cursor-not-allowed opacity-[0.38]",
						)}
					>
						<span className="w-6" aria-hidden>
							{isSelected && <Icon name="check" className="text-[20px]" />}
						</span>
						{year}
					</button>
				);
			})}
		</div>
	);
};

YearPickerList.displayName = "YearPickerList";
